/**
 * @fileoverview Manifesto Page
 * The doctrine of Capitulators.io - nine principles for navigating global liquidity stress.
 * 
 * @module pages/manifesto
 */

import { el } from "../shared/components/simpleRender.js";

/**
 * Manifesto sections data.
 * @type {Array<{number: string, title: string, paragraphs: string[]}>}
 */
const MANIFESTO_SECTIONS = [
  {
    number: "I",
    title: "Liquidity Is Everything",
    paragraphs: [
      "Price discovery is a myth sold to retail. What moves markets is liquidity — the invisible tide that lifts all boats or leaves them stranded on the rocks. Central banks don't set rates; they manipulate the flow. Every rally, every crash, every \"unexpected\" correlation breakdown traces back to the same source: the global liquidity regime.",
      "When liquidity expands, risk assets fly. When it contracts, correlations go to one and everything sells. Understanding this is not optional — it is the prerequisite for survival."
    ]
  },
  {
    number: "II",
    title: "The Dollar Is the Sun",
    paragraphs: [
      "All financial gravity orbits the dollar. EM currencies, commodity prices, cross-border lending, swap lines, Eurodollar funding — everything bends toward King Dollar. A strong dollar is a tightening. A weak dollar is easing. The Fed may control the policy rate, but the dollar index controls the world.",
      "When DXY rises, something somewhere is breaking. The only question is what and when."
    ]
  },
  {
    number: "III",
    title: "Stress Precedes Crisis",
    paragraphs: [
      "Markets don't crash out of blue skies. They crack first — in funding markets, in basis swaps, in repo rates, in the plumbing that retail never sees. By the time it hits the headlines, the informed money is already positioned.",
      "Our job is to watch the stress before it becomes the story. To see the hairline fractures before the earthquake. To measure the pressure building in the pipes."
    ]
  },
  {
    number: "IV",
    title: "Data Over Narrative",
    paragraphs: [
      "CNBC has a narrative. Twitter has a narrative. Every talking head has a story that explains why today's move makes sense. But narratives are post-hoc rationalizations. They're comfort blankets for people who can't read the data.",
      "We don't trade stories. We track signals. The cross-currency basis doesn't lie. The TGA balance doesn't lie. The reverse repo facility doesn't lie. The data is the data. Everything else is entertainment."
    ]
  },
  {
    number: "V",
    title: "Central Banks Are Not Your Friends",
    paragraphs: [
      "The Fed exists to maintain financial stability — for the system, not for you. Their mandate is price stability and maximum employment, but their actual job is to prevent systemic collapse. When those goals conflict with your portfolio, guess which one wins.",
      "\"Don't fight the Fed\" is only half the truth. The full truth is: understand what the Fed is actually fighting for, and position accordingly."
    ]
  },
  {
    number: "VI",
    title: "Correlations Break at the Worst Time",
    paragraphs: [
      "\"Diversification\" is the only free lunch in finance — until it isn't. In a liquidity crisis, correlations spike. Safe havens become exit liquidity. The 60/40 portfolio becomes a synchronized drawdown. Hedges that worked for a decade fail spectacularly.",
      "The time to stress-test your book is not during the stress. It's now."
    ]
  },
  {
    number: "VII",
    title: "Capitulation Is a Process, Not an Event",
    paragraphs: [
      "The bottom doesn't ring a bell. It rings a series of bells, each louder than the last, as leverage is unwound, margin calls cascade, and forced sellers become price-insensitive. Capitulation isn't a single candle — it's a phase. And it has tells.",
      "We study those tells. We track the indicators. We know the playbook because we've seen it before, in 2008, in 2020, in every crisis that came before."
    ]
  },
  {
    number: "VIII",
    title: "The Map Is Not the Territory",
    paragraphs: [
      "Models are useful until they aren't. Every indicator has edge until it becomes crowded. Every signal works until the regime changes. Humility is not optional in this business. The moment you think you've figured it out is the moment the market teaches you otherwise.",
      "We build tools, not oracles. We track probabilities, not certainties. And we update our priors when the evidence demands it."
    ]
  },
  {
    number: "IX",
    title: "We Are the Watchers",
    paragraphs: [
      "This is not a cult. This is not a community of perma-bears praying for collapse. We don't want capitulation — we prepare for it. We are professionals, students, and observers who understand that markets are cyclical, liquidity is finite, and stress always returns.",
      "We built Capitulators.io because no one else was tracking what matters. Because the mainstream ignores the plumbing until it floods. Because someone needs to watch the gauges while everyone else watches the price."
    ],
    coda: "Welcome to the station. The signals are live. The stress is building.",
    final: "Stay vigilant."
  }
];

/**
 * Creates a manifesto section element.
 */
function createManifestoSection(section, index) {
  const paragraphs = section.paragraphs.map(text => 
    el("p", {}, [text])
  );

  // Add coda and final if present (Section IX)
  if (section.coda) {
    paragraphs.push(
      el("p", { className: "manifesto-section-coda" }, [section.coda])
    );
  }
  if (section.final) {
    paragraphs.push(
      el("p", { className: "manifesto-section-final" }, [
        el("em", {}, [section.final])
      ])
    );
  }

  return el("section", { 
    className: "manifesto-section reveal-section",
    "data-reveal-index": String(index)
  }, [
    el("div", { className: "manifesto-section-number" }, [section.number]),
    el("h2", { className: "manifesto-section-title" }, [section.title]),
    el("div", { className: "manifesto-section-body" }, paragraphs)
  ]);
}

/**
 * Renders the Manifesto page.
 * @returns {HTMLElement}
 */
export function renderManifestoPage() {
  return el("div", { className: "manifesto-page" }, [
    // Background layer
    el("div", { className: "manifesto-bg-layer", "aria-hidden": "true" }, []),
    el("div", { className: "manifesto-overlay", "aria-hidden": "true" }, []),

    // Hero Section
    el("header", { className: "manifesto-hero" }, [
      el("div", { className: "manifesto-hero-content" }, [
        el("p", { className: "manifesto-hero-label" }, ["THE DOCTRINE"]),
        el("h1", { className: "manifesto-hero-title" }, ["Manifesto"]),
        el("p", { className: "manifesto-hero-tagline" }, [
          "Nine principles for navigating the liquidity abyss."
        ])
      ]),
      el("div", { className: "manifesto-scroll-indicator", "aria-hidden": "true" }, [
        el("span", { className: "scroll-arrow" }, ["↓"]),
        el("span", { className: "scroll-text" }, ["Scroll to read"])
      ])
    ]),

    // Main Content
    el("main", { className: "manifesto-main", id: "manifesto-content" }, [
      el("article", { className: "manifesto-article" }, [
        // Preamble
        el("section", { className: "manifesto-preamble reveal-section" }, [
          el("p", { className: "manifesto-preamble-text" }, [
            "This is not investment advice. This is not financial guidance. This is a declaration of principles for those who watch the markets with clear eyes and steady nerves — who understand that liquidity is the lifeblood of the system, and when it drains, everything changes."
          ])
        ]),

        // Sections I-IX
        ...MANIFESTO_SECTIONS.map((section, index) => createManifestoSection(section, index)),

        // Footer CTA
        el("footer", { className: "manifesto-footer reveal-section" }, [
          el("div", { className: "manifesto-cta" }, [
            el("a", { href: "#/home", className: "manifesto-btn manifesto-btn-primary" }, [
              "Enter the Station"
            ]),
            el("a", { href: "#/observatories", className: "manifesto-btn manifesto-btn-ghost" }, [
              "View Observatories"
            ])
          ]),
          el("p", { className: "manifesto-disclaimer" }, [
            "This manifesto does not constitute investment advice. Past capitulation is no guarantee of future capitulation. But let's be honest — it probably is."
          ])
        ])
      ])
    ])
  ]);
}

/**
 * Initializes scroll reveal effects after DOM insertion.
 */
export function initManifestoEffects() {
  const sections = document.querySelectorAll(".reveal-section");
  if (!sections.length) return;

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReducedMotion) {
    sections.forEach(section => section.classList.add("is-visible"));
    return;
  }

  // Setup IntersectionObserver for scroll reveals
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      }
    });
  }, {
    root: null,
    rootMargin: "0px 0px -80px 0px",
    threshold: 0.1
  });

  sections.forEach(section => observer.observe(section));

  // Hide scroll indicator on scroll
  const scrollIndicator = document.querySelector(".manifesto-scroll-indicator");
  if (scrollIndicator) {
    let hidden = false;
    const onScroll = () => {
      if (!hidden && window.scrollY > 100) {
        scrollIndicator.style.opacity = "0";
        hidden = true;
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
  }
}

export { MANIFESTO_SECTIONS };
